import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import Chart3D from '../components/Chart3D'
import Chart2D from '../components/Chart2D'
import { 
  PlanetNames, 
  ZodiacSignNames, 
  CyberPlanetNames,
  CyberHouseNames,
  ChartTypeNames,
  ChartType,
  type ZodiacSign,
  type Planet
} from '../types'
import { 
  ArrowLeftIcon, 
  ShareIcon, 
  SparklesIcon, 
  BookmarkIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UsersIcon,
  CpuChipIcon,
  StarIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline'

// 行星对应颜色和图标
const planetStyles: Record<string, { color: string; icon: string; desc: string }> = {
  sun: { color: '#FFD700', icon: '☉', desc: '核心身份与意志' },
  moon: { color: '#C0C0C0', icon: '☽', desc: '情感需求与本能' },
  mercury: { color: '#87CEEB', icon: '☿', desc: '思维与沟通方式' },
  venus: { color: '#FFB6C1', icon: '♀', desc: '爱情与审美偏好' },
  mars: { color: '#FF6347', icon: '♂', desc: '行动力与欲望' },
  jupiter: { color: '#9370DB', icon: '♃', desc: '成长与机遇' },
  saturn: { color: '#708090', icon: '♄', desc: '责任与限制' },
  uranus: { color: '#00CED1', icon: '♅', desc: '变革与创新' },
  neptune: { color: '#4682B4', icon: '♆', desc: '灵性理想' },
  pluto: { color: '#800080', icon: '♇', desc: '深度转化' },
}

// 宫位颜色
const houseColors = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD',
  '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA'
]

export default function ChartDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [chart, setChart] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'overview' | 'planets' | 'houses' | 'aspects' | 'ai'>('overview')
  const [chartView, setChartView] = useState<'2d' | '3d'>('2d') // 默认2D更适合女性用户
  const [showShareModal, setShowShareModal] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!id) return
    const chartData = localStorage.getItem(`chart_${id}`)
    if (chartData) {
      setChart(JSON.parse(chartData))
    }
    setLoading(false)
  }, [id])

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/s/${id}`
    navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleSave = async () => {
    if (!isAuthenticated) {
      const authUrl = `https://auth.7e.ink/oauth/authorize?client_id=astro&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/callback')}&response_type=code`
      window.location.href = authUrl
      return
    }
    // TODO: 调用 API 保存
    alert('保存功能开发中...')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="cyber-loader" />
      </div>
    )
  }

  if (!chart) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-cyber-blue/10 flex items-center justify-center">
            <StarIcon className="w-10 h-10 text-cyber-blue" />
          </div>
          <p className="text-xl text-white mb-2">星盘不存在或已过期</p>
          <p className="text-gray-400 mb-6">未登录的星盘将在24小时后自动删除</p>
          <button onClick={() => navigate('/chart')} className="cyber-button">
            重新生成
          </button>
        </div>
      </div>
    )
  }

  const chartType = (chart.chart_type || 'natal') as ChartType
  const typeInfo = ChartTypeNames[chartType]
  const isDoubleChart = ['synastry', 'composite', 'davison'].includes(chartType)

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-lg bg-cyber-dark border border-cyber-blue/20 flex items-center justify-center text-gray-400 hover:text-cyber-blue hover:border-cyber-blue/50 transition-all"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">{typeInfo.icon}</span>
                <h1 className="text-xl md:text-2xl font-bold font-cyber text-white">
                  {chart.name || `${typeInfo.name}`}
                </h1>
              </div>
              <p className="text-sm text-gray-400">{typeInfo.desc}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowShareModal(true)}
              className="cyber-button-outline flex items-center gap-2 text-sm"
            >
              <ShareIcon className="w-4 h-4" />
              分享
            </button>
            <button
              onClick={handleSave}
              className="cyber-button flex items-center gap-2 text-sm"
            >
              <BookmarkIcon className="w-4 h-4" />
              {isAuthenticated ? '保存' : '登录保存'}
            </button>
          </div>
        </div>
      </div>

      {/* 出生信息卡片 */}
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <div className="cyber-card p-4 md:p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyber-blue/10 flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-cyber-blue" />
              </div>
              <div>
                <p className="text-xs text-gray-500">主角</p>
                <p className="text-sm text-white font-medium">{chart.name || '未命名'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyber-purple/10 flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-cyber-purple" />
              </div>
              <div>
                <p className="text-xs text-gray-500">出生日期</p>
                <p className="text-sm text-white font-medium">{chart.birth_date}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyber-pink/10 flex items-center justify-center">
                <ClockIcon className="w-5 h-5 text-cyber-pink" />
              </div>
              <div>
                <p className="text-xs text-gray-500">出生时间</p>
                <p className="text-sm text-white font-medium">{chart.birth_time}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyber-green/10 flex items-center justify-center">
                <MapPinIcon className="w-5 h-5 text-cyber-green" />
              </div>
              <div>
                <p className="text-xs text-gray-500">出生地点</p>
                <p className="text-sm text-white font-medium truncate">{chart.birth_location}</p>
              </div>
            </div>
          </div>
          
          {/* 第二人信息（双人盘） */}
          {isDoubleChart && chart.person2_birth_date && (
            <div className="mt-4 pt-4 border-t border-cyber-blue/10">
              <div className="flex items-center gap-2 mb-3">
                <UsersIcon className="w-4 h-4 text-cyber-pink" />
                <span className="text-sm text-cyber-pink font-cyber">第二主角</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyber-pink/10 flex items-center justify-center">
                    <UserIcon className="w-5 h-5 text-cyber-pink" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">姓名</p>
                    <p className="text-sm text-white font-medium">{chart.person2_name || '未命名'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-cyber-dark flex items-center justify-center">
                    <CalendarIcon className="w-4 h-4 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">出生日期</p>
                    <p className="text-sm text-white">{chart.person2_birth_date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-cyber-dark flex items-center justify-center">
                    <ClockIcon className="w-4 h-4 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">出生时间</p>
                    <p className="text-sm text-white">{chart.person2_birth_time}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-cyber-dark flex items-center justify-center">
                    <MapPinIcon className="w-4 h-4 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">出生地点</p>
                    <p className="text-sm text-white truncate">{chart.person2_birth_location}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tab 导航 */}
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'overview', label: '概览', icon: StarIcon },
            { id: 'planets', label: '行星', icon: CpuChipIcon },
            { id: 'houses', label: '宫位', icon: MapPinIcon },
            { id: 'aspects', label: '相位', icon: ShareIcon },
            { id: 'ai', label: 'AI解读', icon: SparklesIcon },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-cyber-blue/20 text-cyber-blue border border-cyber-blue/30'
                  : 'bg-cyber-dark/50 text-gray-400 border border-transparent hover:border-cyber-blue/20'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 内容区域 */}
      <div className="max-w-7xl mx-auto px-4">
        {/* 概览页 */}
        {activeTab === 'overview' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* 星盘视图 - 2D/3D切换 */}
            <div className="cyber-card p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-cyber text-white">
                  {chartView === '2d' ? '经典星盘' : '3D 星盘'}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setChartView('2d')}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${
                      chartView === '2d'
                        ? 'bg-cyber-blue text-white'
                        : 'bg-cyber-dark text-gray-400 hover:text-white'
                    }`}
                  >
                    2D
                  </button>
                  <button
                    onClick={() => setChartView('3d')}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${
                      chartView === '3d'
                        ? 'bg-cyber-blue text-white'
                        : 'bg-cyber-dark text-gray-400 hover:text-white'
                    }`}
                  >
                    3D
                  </button>
                </div>
              </div>
              <div className="flex justify-center">
                {chartView === '2d' ? (
                  <Chart2D chartData={chart.chart_data} size={400} />
                ) : (
                  <Chart3D chartData={chart.chart_data} />
                )}
              </div>
            </div>

            {/* 关键指标 */}
            <div className="space-y-6">
              {/* 上升星座 */}
              <div className="cyber-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyber-blue to-cyber-purple flex items-center justify-center text-2xl">
                    🌅
                  </div>
                  <div>
                    <h3 className="text-lg font-cyber text-white">上升星座</h3>
                    <p className="text-sm text-gray-500">你的外在形象与第一印象</p>
                  </div>
                </div>
                <div className="text-center py-6">
                  <p className="text-4xl font-bold text-gradient mb-2">
                    {ZodiacSignNames[chart.chart_data.ascendant.sign as ZodiacSign]}
                  </p>
                  <p className="text-lg text-cyber-blue">
                    {chart.chart_data.ascendant.degree.toFixed(1)}°
                  </p>
                </div>
                <p className="text-sm text-gray-400 text-center">
                  {getAscendantDescription(chart.chart_data.ascendant.sign)}
                </p>
              </div>

              {/* 核心行星分布 */}
              <div className="cyber-card p-6">
                <h3 className="text-lg font-cyber text-white mb-4">核心模块分布</h3>
                <div className="space-y-3">
                  {['sun', 'moon', 'ascendant'].map((key) => {
                    const data = key === 'ascendant' 
                      ? chart.chart_data.ascendant 
                      : chart.chart_data.planets[key]
                    const style = key === 'ascendant' 
                      ? { color: '#FFD700', icon: '🌅', desc: '外在形象' }
                      : planetStyles[key]
                    
                    return (
                      <div key={key} className="flex items-center justify-between p-3 bg-cyber-dark/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{style.icon}</span>
                          <div>
                            <p className="text-sm text-white font-medium">
                              {CyberPlanetNames[key as Planet] || '上升点'}
                            </p>
                            <p className="text-xs text-gray-500">{style.desc}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-white">
                            {ZodiacSignNames[data.sign as ZodiacSign]}
                          </p>
                          <p className="text-xs text-gray-500">
                            {data.degree.toFixed(1)}°
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 行星页 */}
        {activeTab === 'planets' && (
          <div className="cyber-card p-6">
            <h3 className="text-xl font-cyber text-white mb-6">行星位置详情</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(chart.chart_data.planets).map(([key, planet]: [string, any]) => {
                const style = planetStyles[key] || { color: '#888', icon: '●', desc: '' }
                return (
                  <div 
                    key={key} 
                    className="p-4 bg-cyber-dark/50 rounded-xl border border-transparent hover:border-cyber-blue/30 transition-all group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                          style={{ backgroundColor: `${style.color}20` }}
                        >
                          <span style={{ color: style.color }}>{style.icon}</span>
                        </div>
                        <div>
                          <p className="text-lg font-cyber text-white">
                            {CyberPlanetNames[key as Planet]}
                          </p>
                          <p className="text-sm text-gray-500">
                            {PlanetNames[key as Planet]} • {style.desc}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-medium" style={{ color: style.color }}>
                          {ZodiacSignNames[planet.sign as ZodiacSign]}
                        </p>
                        <p className="text-sm text-gray-400">
                          {planet.degree.toFixed(2)}°
                        </p>
                        <p className="text-xs text-gray-500">
                          第{planet.house}宫
                        </p>
                      </div>
                    </div>
                    {planet.retrograde && (
                      <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 rounded bg-cyber-pink/10 text-cyber-pink text-xs">
                        <span>↺</span> 逆行中
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* 宫位页 */}
        {activeTab === 'houses' && (
          <div className="cyber-card p-6">
            <h3 className="text-xl font-cyber text-white mb-6">十二宫位</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {chart.chart_data.houses.map((house: any, index: number) => (
                <div 
                  key={house.number}
                  className="p-4 rounded-xl border border-cyber-blue/10 hover:border-cyber-blue/30 transition-all"
                  style={{ backgroundColor: `${houseColors[index]}10` }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span 
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
                        style={{ backgroundColor: houseColors[index], color: '#000' }}
                      >
                        {house.number}
                      </span>
                      <span className="text-sm text-gray-400 font-mono">
                        {CyberHouseNames[house.number]}
                      </span>
                    </div>
                    <span className="text-lg">{ZodiacSignNames[house.sign as ZodiacSign]}</span>
                  </div>
                  <p className="text-sm text-gray-400">
                    {getHouseDescription(house.number)}
                  </p>
                  <p className="text-xs text-gray-500 mt-2 font-mono">
                    {house.degree.toFixed(1)}° {house.sign}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 相位页 */}
        {activeTab === 'aspects' && (
          <div className="cyber-card p-6">
            <h3 className="text-xl font-cyber text-white mb-6">行星相位</h3>
            <div className="space-y-3">
              {chart.chart_data.aspects.map((aspect: any, index: number) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 bg-cyber-dark/50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-cyber-blue">
                        {CyberPlanetNames[aspect.planet1 as Planet] || aspect.planet1}
                      </span>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      aspect.orb < 3 ? 'bg-cyber-pink/20 text-cyber-pink' :
                      aspect.orb < 6 ? 'bg-cyber-purple/20 text-cyber-purple' :
                      'bg-gray-700 text-gray-400'
                    }`}>
                      {getAspectSymbol(aspect.aspect)}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-cyber-purple">
                        {CyberPlanetNames[aspect.planet2 as Planet] || aspect.planet2}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400">
                      容许度 {aspect.orb.toFixed(1)}°
                    </p>
                    <p className="text-xs text-gray-500">
                      {getAspectDescription(aspect.aspect)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI解读页 */}
        {activeTab === 'ai' && <AIInterpretation chart={chart} />}
      </div>

      {/* 分享弹窗 */}
      {showShareModal && (
        <ShareModal 
          chart={chart} 
          onClose={() => setShowShareModal(false)} 
          copied={copied}
          onCopy={handleCopyLink}
        />
      )}
    </div>
  )
}

// AI解读组件
function AIInterpretation({ chart }: { chart: any }) {
  const [loading, setLoading] = useState(false)
  const [interpretation, setInterpretation] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tokens, setTokens] = useState<number>(0)

  const generateInterpretation = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/interpretations/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chart_data: chart.chart_data,
          type: 'temp'
        }),
      })
      
      const data = await response.json()
      
      if (data.success) {
        setInterpretation(data.data.interpretation)
        setTokens(data.data.tokens_used)
      } else {
        setError(data.error?.message || '生成失败，请重试')
      }
    } catch (err) {
      setError('网络错误，请检查连接')
    } finally {
      setLoading(false)
    }
  }

  if (!interpretation && !loading) {
    return (
      <div className="cyber-card p-8 text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyber-blue to-cyber-purple flex items-center justify-center">
          <SparklesIcon className="w-10 h-10 text-white" />
        </div>
        <h3 className="text-2xl font-cyber text-white mb-4">AI 智能解读</h3>
        <p className="text-gray-400 mb-6 max-w-md mx-auto">
          基于你的星盘数据，AI 将生成个性化的赛博风格解读报告，包含性格分析、职业建议、情感指导等多个维度。
        </p>
        <button onClick={generateInterpretation} className="cyber-button">
          <SparklesIcon className="w-5 h-5 inline mr-2" />
          生成 AI 解读
        </button>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="cyber-card p-12 text-center">
        <div className="cyber-loader mx-auto mb-6" />
        <p className="text-cyber-blue font-cyber animate-pulse">AI 分析中...</p>
        <p className="text-sm text-gray-500 mt-2">正在解析星盘数据</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="cyber-card p-8 text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-cyber-pink/10 flex items-center justify-center">
          <span className="text-4xl">⚠️</span>
        </div>
        <h3 className="text-xl font-cyber text-cyber-pink mb-4">生成失败</h3>
        <p className="text-gray-400 mb-6">{error}</p>
        <button onClick={generateInterpretation} className="cyber-button-outline">
          重新生成
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="cyber-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-cyber text-white">🌟 AI 星盘解读</h3>
          {tokens > 0 && (
            <span className="text-xs text-gray-500 font-mono">
              消耗 {tokens} tokens
            </span>
          )}
        </div>
        <div className="prose prose-invert max-w-none">
          <div className="text-gray-300 leading-relaxed whitespace-pre-wrap">
            {interpretation}
          </div>
        </div>
      </div>
      
      <div className="text-center">
        <button onClick={generateInterpretation} className="cyber-button-outline text-sm">
          <SparklesIcon className="w-4 h-4 inline mr-2" />
          重新生成
        </button>
      </div>
    </div>
  )
}

// 分享弹窗
function ShareModal({ chart, onClose, copied, onCopy }: { 
  chart: any
  onClose: () => void
  copied: boolean
  onCopy: () => void 
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="cyber-card w-full max-w-md p-6 relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕
        </button>
        <h3 className="text-xl font-cyber text-white mb-6">分享星盘</h3>
        
        {/* 卡片预览 */}
        <div className="bg-gradient-to-br from-cyber-blue/20 to-cyber-purple/20 rounded-xl p-6 mb-6 border border-cyber-blue/30">
          <div className="text-center">
            <p className="text-3xl mb-2">{chart.name || '我的星盘'}</p>
            <p className="text-cyber-blue text-sm">
              {chart.birth_date} • {ZodiacSignNames[chart.chart_data?.ascendant?.sign as ZodiacSign]}
            </p>
          </div>
        </div>

        {/* 分享选项 */}
        <div className="space-y-3">
          <button 
            onClick={onCopy}
            className="w-full p-4 bg-cyber-dark/50 rounded-lg flex items-center gap-3 hover:bg-cyber-dark transition-colors"
          >
            <DocumentDuplicateIcon className="w-5 h-5 text-cyber-blue" />
            <span className="flex-1 text-left">{copied ? '✓ 已复制链接' : '复制分享链接'}</span>
          </button>
          
          <button className="w-full p-4 bg-cyber-dark/50 rounded-lg flex items-center gap-3 hover:bg-cyber-dark transition-colors">
            <QrCodeIcon className="w-5 h-5 text-cyber-purple" />
            <span className="flex-1 text-left">生成二维码</span>
          </button>
          
          <button className="w-full p-4 bg-cyber-dark/50 rounded-lg flex items-center gap-3 hover:bg-cyber-dark transition-colors">
            <ArrowDownTrayIcon className="w-5 h-5 text-cyber-pink" />
            <span className="flex-1 text-left">下载分享卡片</span>
          </button>
        </div>
      </div>
    </div>
  )
}

// 辅助函数
function getAscendantDescription(sign: string): string {
  const descs: Record<string, string> = {
    Aries: '充满活力，给人果断自信的第一印象',
    Taurus: '稳重踏实，给人可靠值得信赖的感觉',
    Gemini: '机智灵活，给人聪明善变的感觉',
    Cancer: '温和敏感，给人亲切温暖的感觉',
    Leo: '阳光自信，给人热情大方的印象',
    Virgo: '细致认真，给人专业可靠的印象',
    Libra: '优雅得体，给人和谐美好的感觉',
    Scorpio: '神秘深邃，给人强烈深刻的印象',
    Sagittarius: '开朗乐观，给人自由奔放的感觉',
    Capricorn: '成熟稳重，给人踏实认真的印象',
    Aquarius: '独特前卫，给人创新独立的感觉',
    Pisces: '温柔梦幻，给人艺术气质的感觉',
  }
  return descs[sign] || '独特的个性魅力'
}

function getHouseDescription(house: number): string {
  const descs: Record<number, string> = {
    1: '自我形象、个性、外貌、人生态度',
    2: '财务、价值观、物质资源、自我价值',
    3: '沟通、学习、兄弟姐妹、短途旅行',
    4: '家庭、根源、内心世界、晚年生活',
    5: '创意、恋爱、子女、娱乐、投机',
    6: '工作、健康、服务、日常生活',
    7: '婚姻、合作、公开的敌人、一对一关系',
    8: '转化、共享资源、深层心理、生死',
    9: '高等教育、长途旅行、哲学、信仰',
    10: '事业、公众形象、成就、社会地位',
    11: '友谊、社群、理想、未来愿景',
    12: '潜意识、秘密、灵性、幕后工作',
  }
  return descs[house] || ''
}

function getAspectSymbol(aspect: string): string {
  const symbols: Record<string, string> = {
    conjunction: '☌ 合相',
    sextile: '⚹ 六分相',
    square: '□ 四分相',
    trine: '△ 三分相',
    opposition: '☍ 对分相',
  }
  return symbols[aspect] || aspect
}

function getAspectDescription(aspect: string): string {
  const descs: Record<string, string> = {
    conjunction: '融合、强化',
    sextile: '和谐、机会',
    square: '挑战、张力',
    trine: '天赋、流畅',
    opposition: '对立、平衡',
  }
  return descs[aspect] || ''
}
